/**
 * A PLUGIN'S SETUP FLOW (PLUGINS.md) — the ONE generic dialog for the one-time setup a plugin contributes to a
 * subscription profile. Everything it shows comes from the plugin (``GET /tools/plugins/{id}/setup/{flow}``):
 * ``{title, intro, steps: [{id, title, body, script?, openUrl?, openLabel?}], downloads: [{label, fileName, content,
 * hint?}], connection: {toolName, config, scope}}``. Check = the platform keeps ONE workspace connection for the profile
 * (made once, then linked), and tests it — the plugin's own check decides "ready". The platform names no vendor here.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Copy, Loader2, XCircle } from 'lucide-react';
import { getPluginSetup, createSecretsRegistrySecret, testSecretsRegistrySecret } from '../services/toolsApi.js';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';

function Step({ n, step }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(step.script || ''); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard blocked */ }
  };
  return (
    <div className="rounded-md border border-gray-200 p-3" data-testid={`plugin-setup-step-${step.id || n}`}>
      <p className="text-xs font-semibold text-gray-800">{n}. {step.title}</p>
      {step.body ? <p className="mt-0.5 text-[11px] text-gray-600">{step.body}</p> : null}
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        {step.openUrl ? (
          <a href={step.openUrl} target="_blank" rel="noreferrer" className="text-[11px] font-medium text-indigo-700 hover:underline">
            {step.openLabel || 'Open'}</a>
        ) : null}
        {step.script ? (
          <>
            <button type="button" onClick={copy} data-testid={`plugin-setup-copy-${step.id || n}`}
              className={`${btn} inline-flex items-center gap-1 border border-gray-200 text-gray-700 hover:bg-gray-50`}>
              <Copy className="h-3 w-3" aria-hidden />{copied ? 'Copied' : 'Copy the script'}
            </button>
            <details className="text-[11px] text-gray-500"><summary className="cursor-pointer">Read it first</summary>
              <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-2 font-mono text-[10px] text-gray-700">{step.script}</pre>
            </details>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function PluginSetupDialog({ open, flow, expertName = '', profile, params, onLink, onClose }) {
  const [setup, setSetup] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!open || !flow) return;
    setSetup(null); setErr(''); setResult(null);
    getPluginSetup(flow.pluginId, flow.flowId, params || {}).then(setSetup).catch((e) => setErr(e?.message || "Couldn't prepare the setup"));
  }, [open, flow, params]);
  const check = async () => {
    setBusy(true); setErr(''); setResult(null);
    try {
      let cid = profile?.connectionId || '';
      if (!cid) {
        const c = setup.connection || {};
        const d = await createSecretsRegistrySecret({ toolName: c.toolName, secretName: `${expertName} · ${profile?.name || 'profile'}`.slice(0, 120),
          config: c.config || {}, plaintextSecrets: {}, scope: c.scope || { kind: 'TENANT' } });
        cid = d?.secret?.secretId || '';
        if (!cid) throw new Error('Unexpected response from the registry');
        await onLink?.(cid);
      }
      setResult(await testSecretsRegistrySecret(cid, {}));
    } catch (e) { setErr(e?.message || "Couldn't check the setup"); }
    setBusy(false);
  };
  const download = (d) => {
    const url = URL.createObjectURL(new Blob([d.content || ''], { type: 'text/plain' }));
    const a = document.createElement('a'); a.href = url; a.download = d.fileName || 'setup.txt'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const steps = setup?.steps || [];
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="plugin-setup"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
          <motion.div className="flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
            initial={{ scale: 0.98, y: 6 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.98, y: 6 }}>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">{setup?.title || flow?.title || 'Set up'}</h3>
                <p className="mt-0.5 text-xs text-gray-600">
                  {expertName}{profile?.name ? ` · ${profile.name}` : ''}{setup?.intro ? ` — ${setup.intro}` : ''}
                </p>
              </div>
              {!setup && !err ? <p className="text-xs text-gray-400"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" aria-hidden />Preparing…</p> : null}
              {setup ? (
                <>
                  {steps.map((s, i) => <Step key={s.id || i} n={i + 1} step={s} />)}
                  {(setup.downloads || []).map((d) => (
                    <p key={d.fileName} className="text-[11px] text-gray-500">
                      {d.hint ? `${d.hint} ` : ''}
                      <button type="button" onClick={() => download(d)} className="font-medium text-indigo-700 hover:underline"
                        data-testid="plugin-setup-download">{d.label || 'Download'}</button>
                    </p>
                  ))}
                  <div className="rounded-md border border-gray-200 p-3" data-testid="plugin-setup-check-step">
                    <p className="text-xs font-semibold text-gray-800">{steps.length + 1}. Check</p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <button type="button" onClick={check} disabled={busy} data-testid="plugin-setup-check"
                        className={`${btn} inline-flex items-center gap-1 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}>
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}{busy ? 'Checking…' : 'Check'}
                      </button>
                      {result ? (
                        <span className={`inline-flex items-start gap-1 text-xs ${result.ok ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="plugin-setup-result">
                          {result.ok ? <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden /> : <XCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />}
                          {result.message}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </>
              ) : null}
              {err ? <p className="text-[11px] text-rose-600" data-testid="plugin-setup-error">{err}</p> : null}
            </div>
            <div className="flex justify-end border-t border-gray-100 px-5 py-3">
              <button type="button" onClick={onClose} disabled={busy} data-testid="plugin-setup-close"
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50">
                {result?.ok ? 'Done' : 'Close'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** PURE — the setup flow (if any) a profile of this form needs: the first form adapter a plugin gives a setup flow →
 *  ``{pluginId, flowId, adapterId, title, label}``; null when none. ``catalog`` = ``GET /tools/plugins``'s list. */
export function setupFlowForForm(form, catalog) {
  for (const a of (form?.adapters || [])) {
    for (const p of (catalog || [])) {
      const ad = (p.adapters || []).find((x) => x.id === a.id && x.setupFlow);
      if (ad) {
        const f = (p.setupFlows || []).find((x) => x.id === ad.setupFlow) || { id: ad.setupFlow };
        return { pluginId: p.id, flowId: f.id, adapterId: a.id, title: f.title || 'Set up', label: f.label || 'Set up',
          pluginName: p.displayName || p.id, installed: !(p.install && p.install.installed === false) };
      }
    }
  }
  return null;
}
